import Button from '@/components/button';
import { Box, Stack } from '@mui/material';
import FormMessage from '../form-message';
import { FC } from 'react';
import { FormHandle } from '@/hooks/use-form-handle.hook';
import { FormikValues } from 'formik';
import useModal from '@/hooks/use-modal.hook';
import Text from '@/components/text';
import InlineToast from '@/components/toasts/inline';
import Panel from '@/components/panel';
import { createUseStyles } from 'react-jss';
import Tooltip from '@/components/tooltip';

interface Props {
  formHandle: FormHandle<FormikValues>;
  isNew?: boolean;
  onDelete?: (values: FormikValues) => void | Promise<void>;
  deleteName?: string;
  readonly?: boolean;
  size?: 'small' | 'large' | 'medium';
  disableDelete?: boolean;
  disableDeleteTooltip?: string;
  sticky?: boolean;
}

const MARGIN_SIDE = 16;

const useStyles = createUseStyles({
  formButtons: {
    padding: '6px 24px',
    boxShadow:
      '0px -2px 6px -3px rgba(0,0,0,0.1), 0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
    transition: 'box-shadow 0.3s ease',
    marginBottom: 30,
  },

  stickyWrapper: {
    width: `calc(100% + ${MARGIN_SIDE * 2}px)`,
    marginLeft: -MARGIN_SIDE,

    '& $formButtons': {
      borderRadius: 0,
      marginBottom: 0,
    },

    '@media (min-width: 1800px)': {
      width: '100%',
      marginLeft: 0,
      borderRadius: '5px 5px 0px 0px',
    },
  },
});

const FormButtons: FC<Props> = ({
  formHandle,
  isNew = false,
  onDelete,
  deleteName = '',
  readonly = false,
  size = 'large',
  disableDelete = false,
  disableDeleteTooltip = '',
  sticky = true,
}) => {
  const styles = useStyles();

  const {
    canCancel,
    canSubmit,
    handleSubmit,
    handleCancel,
    errors,
    validateOnChange,
    values,
    formMessage,
  } = formHandle;

  const canDelete = !isNew && !!onDelete && !readonly;
  const { openModal } = useModal();

  const handleDelete = () => {
    if (!canDelete) {
      return;
    }
    openModal('confirm', {
      title: `Delete - ${deleteName}`,
      content: (
        <>
          Are you sure you want to delete{' '}
          <Text inline bold>
            {deleteName}
          </Text>
          ? This action cannot be undone.
        </>
      ),
      onClose: (confirm: boolean) => {
        if (confirm) {
          onDelete(values);
        }
      },
    });
  };

  if (readonly) {
    return null;
  }

  const hasFormMessage = !!formMessage?.content;
  const hasErrors = !!errors?.length;

  const PanelEl = (
    <Panel className={styles.formButtons}>
      <Stack
        spacing={4}
        direction="row"
        alignItems="center"
        minHeight={60}
        justifyContent="space-between"
      >
        {canDelete && (
          <Stack direction="row">
            <Tooltip disabled={!disableDelete} title={disableDeleteTooltip}>
              <Button
                icon="delete"
                type="delete"
                label="Delete"
                onClick={() => handleDelete()}
                disabled={disableDelete}
                size="small"
              />
            </Tooltip>
          </Stack>
        )}
        <Stack gap={1} direction="row" justifyContent="flex-end" flexGrow={1}>
          <Box>
            {hasErrors && <FormMessage errors={errors} validateOnChange={validateOnChange} />}
            {!hasErrors && hasFormMessage && (
              <Box>
                <InlineToast level={formMessage.level} message={formMessage.content} nudge />
              </Box>
            )}
          </Box>
          {canCancel && (
            <Button
              label="Cancel"
              disabled={!canCancel}
              onClick={handleCancel}
              type="cancel"
              size={size}
            />
          )}
          <Button
            label={isNew ? 'Create' : 'Save'}
            disabled={!canSubmit}
            onClick={handleSubmit}
            type="submit"
            size={size}
          />
        </Stack>
      </Stack>
    </Panel>
  );

  if (sticky) {
    return (
      <Stack
        direction="column"
        flexGrow={1}
        justifyContent="flex-end"
        position="sticky"
        bottom={0}
        zIndex={10}
        className={styles.stickyWrapper}
      >
        {PanelEl}
      </Stack>
    );
  }

  return PanelEl;
};

export default FormButtons;
